"use client";

import type { ReactNode } from "react";
import type { UiIconId } from "@/lib/game/uiIcons";
import { GameIcon, type GameTone } from "./GameIcon";

const TONE_TILE: Record<GameTone, string> = {
  violet:
    "border-violet-300/30 bg-gradient-to-br from-violet-500/90 via-indigo-600/85 to-violet-900/90 shadow-[0_8px_0_rgba(49,46,129,0.55)]",
  sky: "border-sky-300/30 bg-gradient-to-br from-sky-400/90 via-cyan-500/85 to-sky-900/90 shadow-[0_8px_0_rgba(12,74,110,0.55)]",
  amber:
    "border-amber-200/35 bg-gradient-to-br from-amber-300/95 via-orange-400/90 to-amber-700/90 shadow-[0_8px_0_rgba(146,64,14,0.5)]",
  rose: "border-rose-300/30 bg-gradient-to-br from-rose-400/90 via-pink-500/85 to-rose-900/90 shadow-[0_8px_0_rgba(159,18,57,0.5)]",
  indigo:
    "border-indigo-300/30 bg-gradient-to-br from-indigo-400/90 via-violet-600/85 to-indigo-950/90 shadow-[0_8px_0_rgba(49,46,129,0.55)]",
  emerald:
    "border-emerald-300/30 bg-gradient-to-br from-emerald-400/90 via-teal-500/85 to-emerald-900/90 shadow-[0_8px_0_rgba(6,95,70,0.5)]",
  slate:
    "border-slate-400/25 bg-gradient-to-br from-slate-500/90 via-slate-600/85 to-slate-900/90 shadow-[0_8px_0_rgba(30,41,59,0.55)]",
};

interface GameMenuTileProps {
  icon: UiIconId;
  label: string;
  subtitle?: string;
  tone?: GameTone;
  onClick?: () => void;
  disabled?: boolean;
  badge?: ReactNode;
}

export function GameMenuTile({
  icon,
  label,
  subtitle,
  tone = "violet",
  onClick,
  disabled,
  badge,
}: GameMenuTileProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={subtitle ? `${label}. ${subtitle}` : label}
      className={`game-menu-tile group relative flex flex-col items-center justify-center gap-2 rounded-3xl border-4 p-4 text-center transition enabled:active:translate-y-1 enabled:active:shadow-[0_4px_0_rgba(0,0,0,0.35)] disabled:cursor-not-allowed disabled:opacity-45 ${TONE_TILE[tone]}`}
    >
      {badge ? (
        <span className="absolute right-2 top-2 rounded-full bg-white/95 px-2 py-0.5 text-[10px] font-extrabold text-slate-800 shadow">
          {badge}
        </span>
      ) : null}
      <span className="flex h-16 w-16 items-center justify-center rounded-2xl border-2 border-white/25 bg-black/15 shadow-inner">
        <GameIcon icon={icon} size="xl" />
      </span>
      <span className="text-sm font-extrabold leading-tight text-white drop-shadow-[0_2px_0_rgba(0,0,0,0.35)]">
        {label}
      </span>
      {subtitle ? (
        <span className="line-clamp-2 text-[11px] font-semibold leading-snug text-white/80">
          {subtitle}
        </span>
      ) : null}
    </button>
  );
}
